/**
 * CallerMatchPage · v2.8+E337 · Dialpad Screen-Pop landing
 * ═══════════════════════════════════════════════════════════════════
 * Dialpad opens this page (`/dashboard/caller-match?phone=<number>`) the
 * instant a call rings. It resolves the caller against our customer +
 * booking records, shows an at-a-glance "Incoming caller" card (identity,
 * flags, recent trips) and hands off into the Dispatcher booking form with
 * one tap — the form auto-fills the caller (or opens New Customer mode for
 * an unknown number). It also auto-polls the active-call state so a
 * ringing→connected transition (or a brand-new call) refreshes live.
 *
 * Read-only: never mutates a customer, booking, or ride-coordinator record.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdmin } from "@/contexts/AdminContext";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  PhoneIncoming, PhoneCall, UserCheck, UserPlus, Loader2, MapPin,
  ArrowRight, AlertTriangle, Car, Clock,
} from "lucide-react";

function formatUsPhone(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(-10);
  if (d.length !== 10) return raw || "";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return ""; }
}

export default function CallerMatchPage() {
  const { admin } = useAdmin();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const phone = (params.get("phone") || "").trim();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [callState, setCallState] = useState("");
  const activePhoneRef = useRef(phone);

  const resolve = useCallback(async (ph, { setActive } = { setActive: true }) => {
    if (!ph) { setError("No phone number provided."); setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/admin/caller-match", {
        params: { phone: ph, set_active: setActive ? 1 : 0 },
      });
      setMatch(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not resolve this caller.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial resolve (marks it the active call so an open form auto-populates).
  useEffect(() => {
    if (!admin) return;
    activePhoneRef.current = phone;
    resolve(phone, { setActive: true });
  }, [admin, phone, resolve]);

  // Auto-poll the live active-call state so ringing → connected (or a fresh
  // inbound call) refreshes the card without a manual reload.
  useEffect(() => {
    if (!admin) return undefined;
    const t = setInterval(async () => {
      if (document.hidden) return;
      try {
        const { data } = await adminApi.get("/admin/dialpad/latest-call");
        if (!data || !data.phone) return;
        setCallState(data.state || "");
        const norm = (s) => String(s || "").replace(/\D/g, "").slice(-10);
        if (norm(data.phone) !== norm(activePhoneRef.current)) {
          // A new caller took over the line — re-resolve without re-marking
          // active (the webhook already did that).
          activePhoneRef.current = data.phone;
          resolve(data.phone, { setActive: false });
        }
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(t);
  }, [admin, resolve]);

  const loadIntoForm = () => {
    try { window.sessionStorage.setItem("rtg_dialpad_force", "1"); } catch { /* ignore */ }
    nav("/admin?tab=driver-mgmt#sub=book-ride");
  };

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-amber-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2"/>Checking session…
      </div>
    );
  }

  const matched = !!match?.matched;
  const cust = match?.customer || {};
  const flags = match?.rider_flags || [];
  const history = match?.booking_history || [];

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 flex items-start justify-center p-4 sm:p-8" data-testid="caller-match-page">
      <Card className="w-full max-w-2xl bg-zinc-950/95 border border-yellow-500/40 p-5 sm:p-6 shadow-2xl shadow-yellow-500/10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-yellow-400 text-zinc-950 flex items-center justify-center shadow-md animate-pulse">
            <PhoneIncoming className="h-6 w-6"/>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-yellow-300 font-bold">
              Incoming Call{callState ? ` · ${callState}` : ""}
            </div>
            <div className="text-xl font-extrabold leading-tight truncate" data-testid="caller-match-phone">
              {formatUsPhone(phone) || "Unknown number"}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-zinc-400" data-testid="caller-match-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2"/>Resolving caller…
          </div>
        ) : error ? (
          <div className="py-8 text-center" data-testid="caller-match-error">
            <AlertTriangle className="h-8 w-8 text-rose-400 mx-auto mb-2"/>
            <p className="text-sm text-rose-300">{error}</p>
          </div>
        ) : (
          <>
            {/* Identity block */}
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                {matched ? (
                  <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" data-testid="caller-match-known">
                    <UserCheck className="h-3 w-3 mr-1"/>Known Customer
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid="caller-match-new">
                    <UserPlus className="h-3 w-3 mr-1"/>New Caller
                  </Badge>
                )}
                {match?.trip_count > 0 && (
                  <span className="text-[11px] text-zinc-400">{match.trip_count} trip{match.trip_count === 1 ? "" : "s"} on file</span>
                )}
              </div>
              {matched ? (
                <div className="space-y-1" data-testid="caller-match-customer">
                  <div className="text-lg font-bold">{cust.name || formatUsPhone(cust.phone)}</div>
                  {cust.email && <div className="text-sm text-zinc-400">{cust.email}</div>}
                  {(cust.address) && (
                    <div className="text-sm text-zinc-400 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-yellow-400"/>{cust.address}</div>
                  )}
                  {cust.customer_type && (
                    <Badge variant="outline" className="mt-1 text-[10px] border-zinc-600 text-zinc-300">{cust.customer_type}</Badge>
                  )}
                </div>
              ) : (
                <p className="text-sm text-zinc-400">
                  No customer record matches this number. Loading it into the Dispatcher form will open
                  <span className="text-amber-300 font-semibold"> New Customer Account Creation</span> with the number pre-filled.
                </p>
              )}
            </div>

            {/* Rider flags */}
            {flags.length > 0 && (
              <div className="rounded-lg border border-rose-500/50 bg-rose-500/10 p-3 mb-4" data-testid="caller-match-flags">
                <div className="text-[10px] uppercase tracking-widest text-rose-300 font-bold flex items-center gap-1 mb-1">
                  <AlertTriangle className="h-3.5 w-3.5"/>Rider Flags
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {flags.map((f, i) => (
                    <span key={i} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white">{f}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Recent trips */}
            {history.length > 0 && (
              <div className="mb-4" data-testid="caller-match-history">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">Recent trips</div>
                <ul className="space-y-1.5">
                  {history.slice(0, 4).map((t, i) => (
                    <li key={i} className="text-[12px] leading-tight border-l-2 border-yellow-500/40 pl-2">
                      <div className="text-zinc-300 truncate">
                        <span className="text-emerald-300">{t.pickup || "—"}</span> <ArrowRight className="h-3 w-3 inline text-zinc-500"/> <span className="text-amber-300">{t.dropoff || "—"}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-1">
                        <Clock className="h-3 w-3"/>{fmtDate(t.date)}{t.status ? ` · ${t.status}` : ""}{t.fare != null ? ` · $${Number(t.fare).toFixed(2)}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={loadIntoForm}
                data-testid="caller-match-load-btn"
                className="flex-1 bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-bold"
              >
                <PhoneCall className="h-4 w-4 mr-1.5"/>
                {matched ? "Load into Dispatcher form" : "Create booking for new caller"}
              </Button>
              <Button
                variant="outline"
                onClick={() => nav("/admin?tab=driver-mgmt#sub=book-ride")}
                data-testid="caller-match-open-console"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Car className="h-4 w-4 mr-1.5"/>Open Console
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
